import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";

interface SignupPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context?: 'profile' | 'chat' | 'save';
  companyName?: string;
  jobTitle?: string;
}

export const SignupPromptDialog = ({ 
  open, 
  onOpenChange, 
  context = 'profile',
  companyName,
  jobTitle,
}: SignupPromptDialogProps) => {
  const navigate = useNavigate();

  const handleSignup = () => {
    onOpenChange(false);
    const returnUrl = window.location.pathname + window.location.search;
    navigate(`/signup/kandidaat?returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  const handleLogin = () => {
    onOpenChange(false);
    const returnUrl = window.location.pathname + window.location.search;
    navigate(`/auth?returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  const titleText =
    context === 'chat' ? 'Direct chatten? 💬'
    : context === 'save' ? 'Vacature opslaan? 🔖'
    : 'Bedrijfsprofiel bekijken? 👀';

  const descriptionText =
    context === 'chat'
      ? `Wil je direct in contact komen${companyName ? ` met ${companyName}` : ''}? Maak gratis een account aan!`
      : context === 'save'
      ? `Bewaar${jobTitle ? ` "${jobTitle}"` : ' deze vacature'} in je persoonlijke lijst en laat bedrijven jou ook vinden via TalentRadar.`
      : `Bekijk het volledige profiel${companyName ? ` van ${companyName}` : ''} en ontdek of het matcht!`;

  const benefits = context === 'save'
    ? [
        { emoji: '🔖', title: 'Bewaar je favoriete vacatures', sub: 'Altijd terug te vinden in jouw lijst' },
        { emoji: '📡', title: 'Sta op TalentRadar', sub: 'Anoniem zichtbaar voor bedrijven die jou zoeken' },
        { emoji: '⚡', title: 'Direct reageren', sub: 'Solliciteer met één klik wanneer je klaar bent' },
      ]
    : [
        { emoji: '🏢', title: 'Bekijk bedrijfsprofielen', sub: 'Ontdek de cultuur en vibe van bedrijven' },
        { emoji: '💬', title: 'Chat direct met werkgevers', sub: 'Geen gedoe, gewoon lekker chatten' },
        { emoji: '✨', title: 'Check onze community', sub: 'Tips, nieuws en chill content' },
      ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-cyan-light to-secondary/30 p-6 pb-4">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-black">
              {titleText}
            </DialogTitle>
            <DialogDescription className="text-base text-foreground/70 pt-2">
              {descriptionText}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Benefits */}
        <div className="p-6 space-y-4">
          <div className="space-y-3">
            {benefits.map((b) => (
              <div key={b.title} className="flex items-center gap-3 p-3 bg-secondary/20 rounded-xl border border-secondary/30">
                <span className="text-xl">{b.emoji}</span>
                <div>
                  <p className="font-bold text-sm">{b.title}</p>
                  <p className="text-xs text-muted-foreground">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="space-y-3 pt-2">
            <Button 
              variant="cta" 
              size="lg" 
              className="w-full rounded-2xl py-6 text-lg group"
              onClick={handleSignup}
            >
              <Zap className="w-5 h-5 mr-2 group-hover:animate-pulse" />
              Maak gratis account aan
            </Button>
            
            <p className="text-sm text-center text-muted-foreground">
              Al een account?{' '}
              <button 
                onClick={handleLogin}
                className="text-primary hover:underline font-medium"
              >
                Log in
              </button>
            </p>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Binnen 1 minuut aangemeld, geen CV nodig 🔥
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};